import type { ComponentChildren } from 'preact';
import { Link, useLocation } from 'wouter-preact';
import { useEffect } from 'preact/hooks';
import type { ClassFilter, Status, Analysis } from '@teacher-shared/analysis';
import { assessments, teachHref, studentHref, useAnalysis, inYear, yearFilter, setYearFilter, datasetOf, type DataState } from '@/lib/teaching/state';
import { bannerOf, type Bucket } from '@/lib/teaching/slice4';

export { useClass } from '@/lib/teaching/state';

export function ClassSwitcher({ value, onChange, classes }: { value: ClassFilter; onChange: (c: ClassFilter) => void; classes: string[] }) {
  const opts: ClassFilter[] = ['all', ...classes];
  return (
    <div class="inline-flex flex-wrap rounded-lg border border-[var(--color-border)] p-0.5 bg-[var(--color-card)]">
      {opts.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          class={[
            'px-3 py-1 rounded-lg text-[12.5px] font-medium transition-colors',
            value === o
              ? 'bg-[var(--color-accent-soft)] font-semibold'
              : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
          ].join(' ')}
          // Template's unlayered `button { color: inherit }` beats Tailwind text utilities.
          style={value === o ? { color: 'var(--color-accent)' } : undefined}
        >
          {o === 'all' ? 'All' : o}
        </button>
      ))}
    </div>
  );
}

/** Year filter for the Teaching header (§05). Changing it moves an
 *  assessment-scoped page to the newest test of that year. */
export function YearSwitcher({ onChange }: { onChange?: (y: number | null) => void }) {
  const [loc, navigate] = useLocation();
  const list = assessments.value ?? [];
  const years = Array.from(new Set(list.map((x) => x.year_level))).sort((a, b) => a - b);
  if (years.length < 2) return null;
  const y = yearFilter();
  return (
    <select
      aria-label="Year"
      value={y === null ? '' : String(y)}
      onChange={(e) => {
        const v = (e.target as HTMLSelectElement).value;
        const next = v ? Number(v) : null;
        setYearFilter(next);
        if (onChange) { onChange(next); return; }
        const shown = next === null ? list : list.filter((x) => x.year_level === next);
        const newest = shown.find((x) => x.stage !== 'setup') ?? shown[0];
        const path = loc.startsWith('/teaching/a/') ? '/teaching' : loc;
        if (newest) navigate(teachHref(path, { a: newest.id }));
      }}
      class="h-9 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-2.5 text-[12.5px] text-[var(--color-text)]"
    >
      <option value="">All years</option>
      {years.map((v) => <option key={v} value={String(v)}>Year {v}</option>)}
    </select>
  );
}

/** Test picker for the Teaching header. Keeps the current page and drops
 *  the class filter, since classes differ between tests. Honours the year filter. */
export function AssessmentSwitcher({ current }: { current: string }) {
  const [loc, navigate] = useLocation();
  const all = assessments.value ?? [];
  const list = inYear(all);
  const cur = all.find((x) => x.id === current);
  // A test opened by link from another year moves the filter to its year.
  const y = yearFilter();
  useEffect(() => { if (cur && y !== null && cur.year_level !== y) setYearFilter(cur.year_level); }, [cur?.id, y]);
  const shown = cur && !list.includes(cur) ? [cur, ...list] : list;
  const path = loc.startsWith('/teaching/a/') ? '/teaching' : loc;
  return (
    <>
      <YearSwitcher />
      {shown.length >= 2 && (
        <select
          aria-label="Test"
          value={current}
          onChange={(e) => navigate(teachHref(path, { a: (e.target as HTMLSelectElement).value }))}
          class="h-9 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-2.5 text-[12.5px] text-[var(--color-text)] max-w-[300px]"
        >
          {shown.map((x) => <option key={x.id} value={x.id}>{x.title} (Y{x.year_level})</option>)}
        </select>
      )}
    </>
  );
}

export function breadcrumbOf(A: Analysis): string {
  const as = A.assessment;
  const date = as.sat_on ? new Date(as.sat_on + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : null;
  return [`Year ${as.year_level}`, as.title, `Criterion ${as.criterion}`, date && `sat ${date}`].filter(Boolean).join(' · ');
}

/** Loading / empty / error states shared by every analysis screen. */
export function WithData({ state, title, children, empty }: { state: DataState; title: string; children: (s: Extract<DataState, { kind: 'ready' }>) => ComponentChildren; empty?: ComponentChildren }) {
  if (state.kind === 'ready') return <>{children(state)}</>;
  if (state.kind === 'empty' && empty) return <>{empty}</>;
  return (
    <TeachingPage title={title} breadcrumb="">
      {state.kind === 'loading' && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {state.kind === 'error' && <div class="text-[13px] text-[#b42318]">{state.message}</div>}
      {state.kind === 'empty' && (
        <Card title="Set up your first test">
          <p class="text-[13px] text-[var(--color-text-muted)] mb-3">Add your classes and students, then define the test's questions. The analysis screens fill in as marks arrive.</p>
          <div class="flex gap-2">
            <Link href="/setup/classes" class="px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-[13px]">Classes &amp; students</Link>
            <Link href="/setup/tests/new" class="px-3 py-1.5 rounded-lg bg-[var(--color-accent)] text-[13px]" style={{ color: '#ffffff' }}>New test</Link>
          </div>
        </Card>
      )}
    </TeachingPage>
  );
}

export { useAnalysis, teachHref, studentHref };

// ── Page shell: big title + own scroll container ──────────────────────

export function TeachingPage({ title, breadcrumb, actions, children, fill, banner }: {
  title: string;
  breadcrumb?: string;
  actions?: ComponentChildren;
  children: ComponentChildren;
  /** Fit the viewport on laptop (cockpit pages), scroll on phone. */
  fill?: boolean;
  banner?: ComponentChildren;
}) {
  return (
    <div class="h-full overflow-y-auto">
      <div class={'px-4 md:px-8 pt-3.5 md:pt-7 pb-6 max-w-[1440px] mx-auto flex flex-col gap-4 md:gap-5 ' + (fill ? 'md:h-full' : '')}>
        <header class="flex flex-wrap items-center gap-x-4 gap-y-2.5">
          {/* Phone: leave room for the menu button at top left. */}
          <div class="min-w-0 pl-12 md:pl-0">
            <h1 class="text-[21px] md:text-[26px] font-semibold tracking-[-0.02em] text-[var(--color-text)] leading-tight">{title}</h1>
            {breadcrumb && <div class="text-[12.5px] md:text-[13.5px] text-[var(--color-text-muted)] mt-0.5">{breadcrumb}</div>}
          </div>
          {actions && <div class="md:ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
        {banner}
        {children}
      </div>
    </div>
  );
}

/** Analysis banner (§06): Provisional / Checkpoint 2 passed / Finalised,
 *  plus the papers-still-marking line (R5). */
export function ProvisionalBanner({ A }: { A: Analysis }) {
  const { fully, of } = A.approval;
  const state = bannerOf(datasetOf(A.assessment.id));
  const marking = of > 0 && fully !== of;
  if (!state && !marking) return null;
  const look = state === 'finalised' ? { bg: '#d3f0d9', bd: '#9fd8ad', text: 'Finalised', sub: 'All three checkpoints passed. Locked until you reopen it.' }
    : state === 'checkpoint_2' ? { bg: '#e8effb', bd: '#b8cdf0', text: 'Checkpoint 2 passed', sub: 'Main class checked. Catch-ups may still change the numbers.' }
    : { bg: '#fff6e0', bd: '#f5c56b', text: 'Not final yet', sub: 'Checkpoint 2 has not been passed, so these numbers may still change.' };
  return (
    <div data-analysis-banner={state ?? 'none'} class="rounded-xl border px-3 py-2 text-[12.5px] flex flex-wrap items-center gap-x-2 gap-y-1" style={{ color: INK, background: look.bg, borderColor: look.bd }}>
      {state && <><b>{look.text}:</b> <span>{look.sub}</span></>}
      {marking && <span>{state ? ' ' : ''}{of - fully} paper{of - fully === 1 ? '' : 's'} still being marked ({fully} of {of} fully marked).</span>}
      {state && <Link href={teachHref('/teaching/checkpoints', { a: A.assessment.id })} class="underline ml-auto" style={{ color: INK }}>Checkpoints</Link>}
    </div>
  );
}

export function Card({ title, sub, right, children, class: cls }: { title?: string; sub?: string; right?: ComponentChildren; children: ComponentChildren; class?: string }) {
  return (
    <div class={'rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 md:p-5 flex flex-col min-h-0 min-w-0 ' + (cls ?? '')}>
      {title && (
        <div class="mb-3 flex items-start gap-2">
          <div class="min-w-0">
            <div class="text-[14px] font-semibold text-[var(--color-text)]">{title}</div>
            {sub && <div class="text-[12px] text-[var(--color-text-muted)] mt-0.5">{sub}</div>}
          </div>
          {right && <div class="ml-auto shrink-0">{right}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

// ── SOP §41 colours: pale fills, always dark text ─────────────────────

export const INK = '#1a1a1a';

/** Pale red → pale amber → pale green, graduated. null = neutral. */
export function perfFill(rate: number | null): string {
  if (rate === null) return 'transparent';
  const stops: [number, [number, number, number]][] = [
    [0, [248, 205, 205]],
    [0.5, [252, 232, 190]],
    [1, [205, 238, 212]],
  ];
  const x = Math.max(0, Math.min(1, rate));
  const [a, b] = x <= 0.5 ? [stops[0], stops[1]] : [stops[1], stops[2]];
  const t = (x - a[0]) / (b[0] - a[0]);
  const c = a[1].map((v, i) => Math.round(v + (b[1][i] - v) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

export const STATUS_STYLE: Record<Status | 'partial' | 'review', { bg: string; label: string }> = {
  completed: { bg: '#d3f0d9', label: 'Paper marked' },
  exempt:    { bg: '#e2e3e6', label: 'Exempt' },
  catchup:   { bg: '#fde9b8', label: 'Catch-up pending' },
  absent:    { bg: '#e2e3e6', label: 'Absent' },
  partial:   { bg: '#fddcbf', label: 'Partial evidence' },
  review:    { bg: '#fddcbf', label: 'Review required' },
};

export function StatusChip({ status }: { status: keyof typeof STATUS_STYLE }) {
  const s = STATUS_STYLE[status];
  return (
    <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-medium whitespace-nowrap" style={{ background: s.bg, color: INK }}>
      {s.label}
    </span>
  );
}

export function PerfChip({ rate, children }: { rate: number | null; children: ComponentChildren }) {
  return (
    <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium tabular-nums" style={{ background: perfFill(rate), color: INK }}>
      {children}
    </span>
  );
}

/** Level distribution buckets: levels on the §41.1 gradient, status
 *  categories in fixed neutral colours (never styled as low). */
export const BUCKET_FILL: Record<Bucket, string> = {
  '0': perfFill(0), '1-2': perfFill(0.25), '3-4': perfFill(0.5), '5-6': perfFill(0.75), '7-8': perfFill(1),
  not_assessable: '#d9dce1', not_yet_confirmed: '#eef0f2',
};

export function pct(r: number | null): string {
  return r === null ? '-' : `${Math.round(r * 100)}%`;
}

/** Horizontal mastery bar with n. Weak needs enough evidence. */
export function RateBar({ label, rate, n, mastered, minN = 10 }: { label: string; rate: number | null; n: number; mastered: number; minN?: number }) {
  const enough = n >= minN;
  return (
    <div class="flex items-center gap-3 py-1">
      <div class="w-[38%] md:w-[30%] text-[12.5px] text-[var(--color-text-muted)] truncate" title={label}>{label}</div>
      <div class="flex-1 h-5 rounded bg-[var(--color-elevated)] overflow-hidden relative">
        {enough && rate !== null && (
          <div class="h-full rounded" style={{ width: `${Math.max(rate * 100, 2)}%`, background: perfFill(rate) }} />
        )}
        <div class="absolute inset-0 flex items-center px-2 text-[11px] font-medium tabular-nums" style={{ color: enough && (rate ?? 0) >= 0.35 ? INK : 'var(--color-text-muted)' }}>
          {enough ? `${pct(rate)} mastery` : 'not enough marked answers'}
        </div>
      </div>
      <div class="w-[64px] text-right text-[11px] text-[var(--color-text-faint)] tabular-nums">{mastered}/{n}</div>
    </div>
  );
}

export function StudentLink({ a, id, name, cls }: { a: string; id: string; name: string; cls?: ClassFilter }) {
  return (
    <Link href={studentHref(a, id, cls)} class="text-[var(--color-accent)] hover:underline">
      {name}
    </Link>
  );
}

export function Legend() {
  return (
    <div class="flex flex-wrap items-center gap-2 text-[11px] text-[var(--color-text-faint)]">
      <span class="px-1.5 py-0.5 rounded" style={{ background: perfFill(1), color: INK }}>Mastered</span>
      <span class="px-1.5 py-0.5 rounded" style={{ background: perfFill(0), color: INK }}>Not yet</span>
      <span class="px-1.5 py-0.5 rounded border border-dashed border-[var(--color-border-strong)]">No answer to mark (not a zero)</span>
      <span class="px-1.5 py-0.5 rounded bg-[var(--color-elevated)]">– not marked yet</span>
    </div>
  );
}

/** One dot per question: green mastered, red not yet, dashed = no evidence. */
export function MasteryDots({ marks, labels }: { marks: (boolean | null)[]; labels?: string[] }) {
  return (
    <div class="flex gap-[3px]">
      {marks.map((m, i) => {
        const tip = labels?.[i] ? `${labels[i]}: ${m === null ? 'no evidence' : m ? 'mastered' : 'not yet'}` : undefined;
        return (
          <i key={i} title={tip} aria-label={tip} class={'block w-[9px] h-[9px] rounded-[3px] ' + (m === null ? 'border border-dashed border-[var(--color-text-faint)]' : '')}
            style={{ background: m === null ? 'transparent' : m ? '#22c55e' : '#ef4444' }} />
        );
      })}
    </div>
  );
}

/** Key for MasteryDots. */
export function DotsLegend({ first, last }: { first: string; last: string }) {
  const sw = (bg: string, dashed = false) => <i class={'inline-block w-[9px] h-[9px] rounded-[3px] align-[-1px] mr-1 ' + (dashed ? 'border border-dashed border-[var(--color-text-faint)]' : '')} style={{ background: bg }} />;
  return (
    <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--color-text-faint)]">
      <span>Dots: {first} to {last}</span>
      <span>{sw('#22c55e')}Mastered</span>
      <span>{sw('#ef4444')}Not yet</span>
      <span>{sw('transparent', true)}No evidence</span>
    </div>
  );
}

/** A misconception code as a small grey tag, shown after its meaning. */
export function CodeTag({ code }: { code: string }) {
  return <span class="inline-block ml-1 px-1 rounded text-[10px] font-medium tabular-nums align-[1px] bg-[var(--color-elevated)] text-[var(--color-text-faint)]">{code}</span>;
}

/** Text whose "[CS01]" markers render as CodeTags. */
export function Plain({ text }: { text: string }) {
  const parts = text.split(/\[([A-Z]{1,4}\d{1,3})\]/);
  return <>{parts.map((p, i) => (i % 2 ? <CodeTag key={i} code={p} /> : p))}</>;
}

/** Plain text for a title attribute: "[CS01]" becomes "(CS01)". */
export const plainTitle = (text: string) => text.replace(/\[([A-Z]{1,4}\d{1,3})\]/g, '($1)');

export function Initials({ name }: { name: string }) {
  const ini = name.split(' ').map((p) => p[0]).slice(0, 2).join('');
  return (
    <div class="w-8 h-8 shrink-0 rounded-full grid place-items-center text-[11px] font-semibold bg-[var(--color-accent-soft)] text-[var(--color-accent)]">{ini}</div>
  );
}
